/**
 * The "Cấp quyền" dialog — Task T2-D. A bordered card (`wp-drawer`, the same shell
 * `PeopleDrawer.tsx` uses for a panel that is not the page's main content) toggled
 * open by `PermissionGrantsScreen`'s own button, rather than a form always on
 * screen the way `PoliciesScreen`'s editor is: granting a permission is an
 * occasional, deliberate act, not something every visit to this screen does.
 *
 * ── The fourth widget changes with `subject_type` ────────────────────────────
 *
 * `PermissionGrantRequest.subject_key` is the employee id for `employee`, the role
 * name for `role`, the achau department id for `department`, and free text for
 * `job_title` (the schema's own description) — four different facts needing four
 * different controls, never one text box guessing at the shape. `EmployeePicker`
 * (T2-B) is reused verbatim for the first; a role/department select is fed by data
 * this screen already reads for other reasons (departments, from
 * `GET /employees/departments`); the fourth is the one case with no vocabulary to
 * pick from at all.
 *
 * ── `reason` is required here, though the API accepts it blank ──────────────
 *
 * `PermissionGrantRequest.reason` has no `required` in `openapi.yaml`. It is
 * required on THIS screen because a grant with no stated reason is an audit trail
 * with a hole in it — `wp_audit_log.reason` is the only place a future reviewer
 * reads why an administrator made this exact change, and "why" is exactly the
 * fact an optional field lets people skip under time pressure.
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n } from '../../../i18n';
import type { EmployeeDepartmentFacet } from '../../../api/employees';
import type { PermissionGrantRequest } from '../../../api/permission-grants';
import type { WorkforceApiError } from '../../../api/errors';
import { EmployeePicker, type EmployeePickerValue } from '../../../components/EmployeePicker';
import {
  buildGrantRequest,
  GRANT_ROLE_SUBJECTS,
  PERMISSION_CODE_DESCRIPTION_KEY,
  PERMISSION_CODES_BY_GROUP,
  PERMISSION_GROUPS,
  PERMISSION_GROUP_LABEL_KEY,
  ROLE_SUBJECT_LABEL_KEY,
  SUBJECT_TYPE_LABEL_KEY,
  type GrantDraft,
  type GrantRoleSubject,
} from '../../../permissions/grants';
import { PERMISSION_SUBJECT_TYPES, type PermissionCode } from '../../../api/permission-grants';
import { ApiErrorNote } from '../../../states/ApiErrorNote';

export interface GrantFormProps {
  readonly departments: readonly EmployeeDepartmentFacet[];
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onSubmit: (body: PermissionGrantRequest) => void;
  readonly onCancel: () => void;
}

export function GrantForm({
  departments,
  pending,
  error,
  onSubmit,
  onCancel,
}: GrantFormProps): ReactNode {
  const { t } = useI18n();
  const [subjectType, setSubjectType] = useState<GrantDraft['subjectType']>('employee');
  const [employeeValue, setEmployeeValue] = useState<EmployeePickerValue | null>(null);
  const [roleValue, setRoleValue] = useState<GrantRoleSubject | ''>('');
  const [departmentValue, setDepartmentValue] = useState('');
  const [jobTitleValue, setJobTitleValue] = useState('');
  const [permissionCode, setPermissionCode] = useState<PermissionCode | ''>('');
  const [effect, setEffect] = useState<GrantDraft['effect']>('allow');
  const [reason, setReason] = useState('');
  const [expiresAtLocal, setExpiresAtLocal] = useState('');

  const subjectKey =
    subjectType === 'employee'
      ? (employeeValue?.id ?? '')
      : subjectType === 'role'
        ? roleValue
        : subjectType === 'department'
          ? departmentValue
          : jobTitleValue;

  const draft: GrantDraft = {
    subjectType,
    subjectKey,
    permissionCode,
    effect,
    reason,
    expiresAtLocal,
  };
  const request = buildGrantRequest(draft);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (request === null || pending) return;
    onSubmit(request);
  };

  return (
    <section
      className="wp-drawer"
      role="dialog"
      aria-labelledby="t2d-form-heading"
      data-testid="t2d-form"
    >
      <h3 id="t2d-form-heading">{t('adminPermissions.form.heading')}</h3>

      <form onSubmit={submit} className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="t2d-form-subject-type">
            {t('adminPermissions.form.subjectType.label')}
          </label>
          <select
            id="t2d-form-subject-type"
            className="wp-field__input"
            data-testid="t2d-form-subject-type"
            value={subjectType}
            onChange={(event) => {
              setSubjectType(event.target.value as GrantDraft['subjectType']);
            }}
          >
            {PERMISSION_SUBJECT_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(SUBJECT_TYPE_LABEL_KEY[value])}
              </option>
            ))}
          </select>
        </div>

        {subjectType === 'employee' ? (
          <div className="wp-filters__group">
            <EmployeePicker
              value={employeeValue}
              onChange={setEmployeeValue}
              label={t('adminPermissions.form.employee.label')}
              testId="t2d-form-employee"
            />
          </div>
        ) : null}

        {subjectType === 'role' ? (
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="t2d-form-role">
              {t('adminPermissions.form.role.label')}
            </label>
            <select
              id="t2d-form-role"
              className="wp-field__input"
              data-testid="t2d-form-role"
              value={roleValue}
              onChange={(event) => {
                setRoleValue(event.target.value as GrantRoleSubject | '');
              }}
            >
              <option value="">{t('adminPermissions.form.role.placeholder')}</option>
              {GRANT_ROLE_SUBJECTS.map((value) => (
                <option key={value} value={value}>
                  {t(ROLE_SUBJECT_LABEL_KEY[value])}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {subjectType === 'department' ? (
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="t2d-form-department">
              {t('adminPermissions.form.department.label')}
            </label>
            <select
              id="t2d-form-department"
              className="wp-field__input"
              data-testid="t2d-form-department"
              value={departmentValue}
              onChange={(event) => {
                setDepartmentValue(event.target.value);
              }}
            >
              <option value="">{t('adminPermissions.form.department.placeholder')}</option>
              {/* Same `department_id !== null` filter `PeopleScreen`'s own filter
                  keeps: "no department" cannot be a grant subject. */}
              {departments
                .filter((facet) => facet.department_id !== null)
                .map((facet) => (
                  <option key={facet.department_id} value={facet.department_id ?? ''}>
                    {facet.department_name ?? facet.department_id}
                  </option>
                ))}
            </select>
          </div>
        ) : null}

        {subjectType === 'job_title' ? (
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="t2d-form-job-title">
              {t('adminPermissions.form.jobTitle.label')}
            </label>
            <input
              id="t2d-form-job-title"
              className="wp-field__input"
              type="text"
              data-testid="t2d-form-job-title"
              value={jobTitleValue}
              placeholder={t('adminPermissions.form.jobTitle.placeholder')}
              onChange={(event) => {
                setJobTitleValue(event.target.value);
              }}
            />
          </div>
        ) : null}

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="t2d-form-code">
            {t('adminPermissions.form.permissionCode.label')}
          </label>
          <select
            id="t2d-form-code"
            className="wp-field__input"
            data-testid="t2d-form-code"
            value={permissionCode}
            onChange={(event) => {
              setPermissionCode(event.target.value as PermissionCode | '');
            }}
          >
            <option value="">{t('adminPermissions.form.permissionCode.placeholder')}</option>
            {PERMISSION_GROUPS.map((group) => (
              <optgroup key={group} label={t(PERMISSION_GROUP_LABEL_KEY[group])}>
                {PERMISSION_CODES_BY_GROUP[group].map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {permissionCode === '' ? null : (
            <p className="wp-small wp-muted" data-testid="t2d-form-code-help">
              {t(PERMISSION_CODE_DESCRIPTION_KEY[permissionCode])}
            </p>
          )}
        </div>

        <fieldset className="wp-filters__group">
          <legend className="wp-field__label">{t('adminPermissions.form.effect.label')}</legend>
          <label className="wp-check">
            <input
              type="radio"
              name="t2d-form-effect"
              value="allow"
              checked={effect === 'allow'}
              data-testid="t2d-form-effect-allow"
              onChange={() => {
                setEffect('allow');
              }}
            />{' '}
            {t('adminPermissions.effect.allow')}
          </label>{' '}
          <label className="wp-check">
            <input
              type="radio"
              name="t2d-form-effect"
              value="deny"
              checked={effect === 'deny'}
              data-testid="t2d-form-effect-deny"
              onChange={() => {
                setEffect('deny');
              }}
            />{' '}
            {t('adminPermissions.effect.deny')}
          </label>
        </fieldset>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="t2d-form-reason">
            {t('adminPermissions.form.reason.label')}
          </label>
          <input
            id="t2d-form-reason"
            className="wp-field__input"
            type="text"
            data-testid="t2d-form-reason"
            value={reason}
            aria-describedby="t2d-form-reason-hint"
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          <p className="wp-small wp-muted" id="t2d-form-reason-hint">
            {t('adminPermissions.form.reason.hint')}
          </p>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="t2d-form-expires-at">
            {t('adminPermissions.form.expiresAt.label')}
          </label>
          <input
            id="t2d-form-expires-at"
            className="wp-field__input"
            type="datetime-local"
            data-testid="t2d-form-expires-at"
            value={expiresAtLocal}
            onChange={(event) => {
              setExpiresAtLocal(event.target.value);
            }}
          />
        </div>

        {error === null ? null : (
          <ApiErrorNote
            error={error}
            subject={t('adminPermissions.subject')}
            testId="t2d-form-error"
          />
        )}

        <div className="wp-formbar">
          <button
            type="submit"
            className="wp-button wp-button--primary"
            data-testid="t2d-form-submit"
            disabled={request === null || pending}
          >
            {pending ? t('adminPermissions.form.submitting') : t('adminPermissions.form.submit')}
          </button>
          <button
            type="button"
            className="wp-button wp-button--quiet"
            data-testid="t2d-form-cancel"
            onClick={onCancel}
          >
            {t('adminPermissions.form.cancel')}
          </button>
        </div>
      </form>
    </section>
  );
}
